import { ILocaleList } from 'mod/store/locale/interface';
import { getLocaleList } from 'mod/store/locale/service';
import { getTranslation } from 'base/servicesV2/i18n';
import { IRedirectMap } from 'base/interfacesV2/redirect-map';
import { getRedirectMap } from 'base/servicesV2/redirect-map';
import { INavLists } from 'mod/store/nav/interface';
import { getProductNav } from 'mod/store/nav/service';
import {
    ITagTheme,
    ITagThemeObj,
    ITopPromotionBar,
    ITopStripedBanner
} from 'base/interfacesV2/feature';
import {
    getHomeVersion,
    getTagTheme,
    getTopPromotionBar,
    getTopStripedBanner
} from 'base/servicesV2/feature';
import { IServerClientKeys } from 'mod/store/clientKeys/interface';
import { getServerClientKeys } from 'mod/store/clientKeys/service';
import { getCurrentTagTheme } from 'base/store/utils';
import { fetchReferralConfig } from 'mod/referral/service';
import { IReferralConfig, IServerReferralConfig } from 'mod/referral/interface';
import translationCodes from 'app/locales/codes.json';

export const clearKeys = [
    'all',
    'nav',
    'i18n',
    'locale',
    'uriMap',
    'topStripedBanner',
    'topPromotionBar',
    'tagTheme',
    'referral'
] as const;

export type ITagThemes = ITagTheme & {
    current?: ITagThemeObj // 当前时段的 各个tag 主题配置
}

export type THomePageVersion = 'NEW' | 'OLD';

// 与 persistState 数据一致
export type IServerStateCommon = {
    localeList?: ILocaleList;
    uriMapList?: IRedirectMap;
    translation?: Record<string, Record<string, string>>; //所有语种翻译hack，{en: {code: 'text'}}
};

// persistState 将对国家语言等进行过滤
export type ServerStateType = IServerStateCommon & {
    navList: Record<string, INavLists>; //所有语种国家的商品分类导航，key 为 lang-country
    topStripedBanner: Record<string, ITopStripedBanner>; //key 为 lang-country
    topPromotionBar: Record<string, ITopPromotionBar>; //key 为 lang-country
    tagTheme: Record<string, ITagThemes>; //key 为 lang-country
    debugNodeCache: string; //测试清node缓存
    clientKeys: IServerClientKeys;
    referralConfig: Record<string, IServerReferralConfig>;
    homePageVersions: Record<string, THomePageVersion>; // 首页版本
};

export type ServerStoreType = {
    state: ServerStateType;
    fetchState: (lang: string, country: string) => void;
    clearState: (key: string, envName: string) => void;
    fetchLocaleList: () => Promise<ILocaleList>;
}

const getInitialState = () => ({
    navList: {},
    topStripedBanner: {},
    topPromotionBar: {},
    tagTheme: {},
    clientKeys: {},
    referralConfig: {},
    debugNodeCache: 'no-cache',
    homePageVersions: {},
});

const createServerStore = (serverState?: ServerStateType, debugNodeCache?: string): ServerStoreType => {
    const state = {
        ...getInitialState(),
        ...serverState,
    };
    if (debugNodeCache) {
        state.debugNodeCache = debugNodeCache;
    }

    return {
        state,
        async fetchState(lang, country) {
            const s = this.state;
            const locale = `${lang}-${country}`;

            const [
                localeList,
                uriMapList,
                translation,
                localeNavList,
                localeTopStripedBanner,
                localeTopPromotionBar,
                localeTagTheme,
                clientKeys,
                referralConfig,
                homePageVersion
            ] = await Promise.all([
                // 获取语言货币国家列表
                s.localeList || getLocaleList(),
                // 获取shopify url 与 next url对应关系
                s.uriMapList || getRedirectMap(),
                // 获取当前语言翻译
                s.translation || getTranslation(translationCodes),
                // 获取当前语言导航
                s.navList[locale] || getProductNav(lang),
                // 获取页头活动配置
                s.topStripedBanner[locale] || getTopStripedBanner(),
                // 获取页头促销栏
                s.topPromotionBar[locale] || getTopPromotionBar(),
                // tag 主题
                s.tagTheme[locale] || getTagTheme(),
                // Client Keys
                Object.keys(s.clientKeys).length > 0 ? s.clientKeys : getServerClientKeys(),
                // Referral
                s.referralConfig[country] || fetchReferralConfig(),
                // 首页版本
                s.homePageVersions[country] || getHomeVersion()
            ]);

            const {
                referTask,
                ...restReferralConfig
            } = referralConfig as IReferralConfig;

            this.state = {
                debugNodeCache: s.debugNodeCache,
                localeList,
                uriMapList,
                translation,
                navList: { ...s.navList, [locale]: localeNavList },
                topStripedBanner: { ...s.topStripedBanner, [locale]: localeTopStripedBanner },
                topPromotionBar: { ...s.topPromotionBar, [locale]: localeTopPromotionBar },
                tagTheme: { ...s.tagTheme, [locale]: getCurrentTagTheme(localeTagTheme) },
                clientKeys,
                referralConfig: { ...s.referralConfig, [country]: restReferralConfig },
                homePageVersions: { ...s.homePageVersions, [country]: homePageVersion },
            }
        },
        async clearState(key, envName) {
            switch (key) {
                case 'all':
                    this.state = getInitialState();
                    break;
                case 'locale':
                    this.state.localeList = void 0;
                    break;
                case 'i18n':
                    this.state.translation = void 0;
                    break;
                case 'nav':
                    this.state.navList = {};
                    break;
                case 'uriMap':
                    this.state.uriMapList = void 0;
                    break;
                case 'topStripedBanner':
                    this.state.topStripedBanner = {};
                    break;
                case 'topPromotionBar':
                    this.state.topPromotionBar = {};
                    break;
                case 'tagTheme':
                    this.state.tagTheme = {};
                    break;
                case 'clientKeys':
                    this.state.clientKeys = {};
                case 'referral':
                    this.state.referralConfig = {};
                    break;
            }
        },
        async fetchLocaleList() {
            let localeList = this.state.localeList;
            if (!localeList?.length) {
                localeList = await getLocaleList().catch(() => []);
                this.state.localeList = localeList;
            }
            return localeList;
        }
    }
};

export default createServerStore;